import { DashboardHeader } from "@/components/dashboard-header";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SidebarInset } from "@/components/ui/sidebar";
import { useAuthStore } from "@/stores/useAuthStore";
import instance from "@/lib/axios";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { applyServerErrors } from "@/lib/apply-server-errors";
import {
  changePasswordSchema,
  type ChangePasswordFormValues,
  CHANGE_PASSWORD_FIELDS,
} from "@/schemas/auth";

export const Route = createFileRoute("/user/_layout/change-password")({
  component: ChangePassword,
});

function ChangePassword() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { old_password: "", new_password: "", confirm_password: "" },
  });

  const onValid = async (data: ChangePasswordFormValues) => {
    setIsSubmitting(true);
    try {
      const response = await instance.post("/api/auth/change-password/", {
        old_password: data.old_password,
        new_password: data.new_password,
      });
      toast.success(
        response.data?.message ||
          "Your password has been changed successfully!",
      );
      reset();
      setTimeout(() => {
        useAuthStore.getState().logout();
        window.location.href = "/login";
      }, 600);
      // Redirect to profile or dashboard after success
    } catch (err) {
      const { handled, rest } = applyServerErrors(
        err,
        setError,
        CHANGE_PASSWORD_FIELDS,
      );
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to change password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SidebarInset className="bg-muted/50">
      <DashboardHeader
        breadcrumbs={[
          { label: "Profile Management", href: "/user/profile-management" },
          { label: "Change Password" },
        ]}
      />
      <div className="grid h-full">
        {/* Left side - Form */}
        <div className="flex flex-col gap-4 p-6 md:p-10">
          {/* Form Container */}
          <div className="flex flex-1 flex-col gap-6 items-center justify-center">
            <Card className="w-full max-w-sm text-center shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl font-bold">
                  Change Your Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onValid)} className="space-y-4">
                  {/* Current Password Field */}
                  <div className="grid gap-2 text-left">
                    <Label htmlFor="current-password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="current-password"
                        type={showCurrentPassword ? "text" : "password"}
                        placeholder="Enter your current password"
                        autoComplete="current-password"
                        {...register("old_password")}
                        className={`pr-10 ${
                          errors.old_password
                            ? "border-red-500 focus:border-red-500"
                            : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                      >
                        {showCurrentPassword ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.old_password && (
                      <p className="text-xs text-red-500">
                        {errors.old_password.message}
                      </p>
                    )}
                  </div>

                  {/* New Password Field */}
                  <div className="grid gap-2 text-left">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Enter your new password"
                        autoComplete="new-password"
                        {...register("new_password")}
                        className={`pr-10 ${
                          errors.new_password
                            ? "border-red-500 focus:border-red-500"
                            : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.new_password ? (
                      <p className="text-xs text-red-500">
                        {errors.new_password.message}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Must be at least 8 characters
                      </p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="grid gap-2 text-left">
                    <Label htmlFor="confirm-password">
                      Confirm New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your new password"
                        autoComplete="new-password"
                        {...register("confirm_password")}
                        className={`pr-10 ${
                          errors.confirm_password
                            ? "border-red-500 focus:border-red-500"
                            : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.confirm_password && (
                      <p className="text-xs text-red-500">
                        {errors.confirm_password.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full mt-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Changing Password" : "Change Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <div className="text-muted-foreground text-center text-xs text-balance">
              By clicking continue, you agree to our{" "}
              <Link
                to="/terms-of-service"
                className="underline underline-offset-4 hover:text-primary"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy-policy"
                className="underline underline-offset-4 hover:text-primary"
              >
                Privacy Policy
              </Link>
              .
            </div>
          </div>
        </div>
      </div>
    </SidebarInset>
  );
}
